import { createContext, useContext, useState, ReactNode } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';

export type UpgradeModalMode = 'upgrade' | 'reactivate';

const CANCELED_LIFECYCLES = new Set(['canceled', 'cancelled', 'churned']);

export interface OpenOptions {
  planId?: string;
  mode?: UpgradeModalMode;
}

interface UpgradeModalContextType {
  isOpen: boolean;
  preselectedPlanId?: string;
  mode: UpgradeModalMode;
  /**
   * Accepts either a legacy `planId: string` argument (treated as
   * `{ planId, mode: 'upgrade' }`) or an options object.
   */
  openUpgradeModal: (options?: string | OpenOptions) => void;
  closeUpgradeModal: () => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextType | undefined>(undefined);

export function UpgradeModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [preselectedPlanId, setPreselectedPlanId] = useState<string | undefined>();
  const [mode, setMode] = useState<UpgradeModalMode>('upgrade');
  const { currentOrganization } = useOrganization();

  const openUpgradeModal = (arg?: string | OpenOptions) => {
    const options: OpenOptions =
      typeof arg === 'string' ? { planId: arg } : arg ?? {};
    // If the caller doesn't specify a mode, fall back to reactivate for
    // canceled orgs. /api/billing/upgrade 400s on canceled lifecycles, so
    // defaulting to 'upgrade' sends users into a dead end — the many
    // generic "Upgrade" CTAs across the app (sidebar, status bar, soft-
    // gating, limit warnings) all hit this path.
    const lifecycle = currentOrganization?.lifecycle?.toLowerCase();
    const defaultMode: UpgradeModalMode =
      lifecycle && CANCELED_LIFECYCLES.has(lifecycle) ? 'reactivate' : 'upgrade';
    setPreselectedPlanId(options.planId);
    setMode(options.mode ?? defaultMode);
    setIsOpen(true);
  };

  const closeUpgradeModal = () => {
    setIsOpen(false);
    setPreselectedPlanId(undefined);
    setMode('upgrade');
  };

  return (
    <UpgradeModalContext.Provider
      value={{ isOpen, preselectedPlanId, mode, openUpgradeModal, closeUpgradeModal }}
    >
      {children}
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal() {
  const context = useContext(UpgradeModalContext);
  if (!context) {
    throw new Error('useUpgradeModal must be used within UpgradeModalProvider');
  }
  return context;
}
